import { useSuspenseQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { webConfig } from '@gitbook/env';
import {
    Button,
    Description,
    Dialog,
    DialogBody,
    DialogHeader,
    DialogRoot,
    Heading,
    HeadingContext,
    LoadingIndicator,
    toast,
    useDialogAction,
    useDialogClose,
} from '@gitbook/spine-react';
import { usePersistentValue } from '@gitbook/util-react-hooks';
import { assert, assertNever } from '@gitbook/util-ts';

import { useNavigation, useSetAnalyticsScreen } from '../../../navigation';
import { apiClient, apiQuery, getSiteSpacesFromStructure } from '../../../state';
import { extractCollectionNames } from '../../../utils';
import { CreateSiteSectionDialog } from '../../Site';
import { applyNewSpaceTemplate, SpaceTemplateCard, TemplatesList } from '../../Templates';
import { BlankCard } from './BlankCard';
import { BlankTemplateCard } from './BlankTemplateCard';
import { DuplicateCard } from './DuplicateCard';
import { GitSyncCard } from './GitSyncCard';
import { ImportCard } from './ImportCard';
import { PickExistingSpace, usePickExistingSpaceState } from './PickExistingSpace';
import { TranslationCard } from './TranslationCard';
import type {
    SpaceCreationOnCreateSpace,
    SpaceCreationTarget,
    SpaceCreationTargetSite,
    SpaceCreationTargetSiteSection,
    SpaceCreationTargetSiteVariant,
    SpaceCreationTargetSpace,
} from './types';

export interface SpaceCreationDialogProps {
    organizationId: string;
    collectionId?: string;
    target?: SpaceCreationTarget | undefined;
    defaultOpenTranslation?: boolean;
}

export function SpaceCreationDialog(props: SpaceCreationDialogProps) {
    const { target = { type: 'space' } } = props;

    switch (target.type) {
        case 'space':
            return <SpaceCreationDialogTargetSpace {...props} target={target} />;
        case 'site':
            return <SpaceCreationDialogTargetSite {...props} target={target} />;
        case 'siteSection':
            return <SpaceCreationDialogTargetSection {...props} target={target} />;
        case 'siteVariant':
            return <SpaceCreationDialogTargetVariant {...props} target={target} />;
        default:
            assertNever(target);
    }
}

function SpaceCreationDialogTargetSpace(
    props: Omit<SpaceCreationDialogProps, 'target'> & {
        target: SpaceCreationTargetSpace;
    }
) {
    const { organizationId, collectionId, target, defaultOpenTranslation } = props;
    const [isPending, startDialogAction] = useDialogAction();
    const { navigate } = useNavigation();

    const onCreateSpace: SpaceCreationOnCreateSpace = async (createSpace, finalize) => {
        await startDialogAction(async ({ close }) => {
            await toast.catch(async () => {
                const space = await createSpace({ collectionId });
                if (finalize) {
                    finalize(space);
                } else {
                    navigate({
                        screen: {
                            type: 'space',
                            space: space.id,
                            organization: space.organization,
                            page: '',
                        },
                    });
                }
                await close();
            }, 'Error while creating space');
        });
    };

    return (
        <SpaceCreationDialogContainer isPending={isPending}>
            <DialogHeader>
                <Heading>Create space</Heading>
                <Description>
                    Spaces are where your content lives. Choose where your initial content comes
                    from to get started quickly.
                </Description>
            </DialogHeader>
            <SpaceCreateDialogBody
                organizationId={organizationId}
                target={target}
                onCreateSpace={onCreateSpace}
                isPending={isPending}
                defaultOpenTranslation={defaultOpenTranslation}
            />
        </SpaceCreationDialogContainer>
    );
}

function SpaceCreationDialogTargetSite(
    props: Omit<SpaceCreationDialogProps, 'target'> & {
        target: SpaceCreationTargetSite;
    }
) {
    const { organizationId, target, defaultOpenTranslation } = props;
    const [isPending, startDialogAction] = useDialogAction();

    const onCreateSpace: SpaceCreationOnCreateSpace = async (createSpace, finalize) => {
        await startDialogAction(async ({ close }) => {
            await toast.catch(async () => {
                const { data: collection } = await apiClient.collections.createCollection(
                    organizationId,
                    {
                        title: target.site.title,
                    }
                );
                const space = await createSpace({ collectionId: collection.id });
                await apiClient.siteSpaces.addSpaceToSite(organizationId, target.site.id, {
                    spaceId: space.id,
                });
                if (finalize) {
                    finalize?.(space);
                } else {
                    toast.success('A space has been successfully linked to your site as content', {
                        action: (
                            <Button
                                href={{
                                    screen: {
                                        type: 'space',
                                        space: space.id,
                                        organization: space.organization,
                                        page: '',
                                    },
                                }}
                                label="Go to space"
                            />
                        ),
                    });
                }
                await close();
            }, 'Error while adding space to site');
        });
    };

    return (
        <SpaceCreationDialogContainer isPending={isPending}>
            <DialogHeader>
                <Heading>Add space</Heading>
                <Description>
                    Spaces are where your content lives. Choose where your initial content comes
                    from to get started quickly.
                </Description>
            </DialogHeader>
            <SpaceCreateDialogBody
                organizationId={organizationId}
                target={target}
                onCreateSpace={onCreateSpace}
                isPending={isPending}
                defaultOpenTranslation={defaultOpenTranslation}
            />
        </SpaceCreationDialogContainer>
    );
}

function SpaceCreationDialogTargetSection(
    props: Omit<SpaceCreationDialogProps, 'target'> & {
        target: SpaceCreationTargetSiteSection;
    }
) {
    const { organizationId, target, defaultOpenTranslation } = props;
    // If collectionId is not provided, we try to find the first space in the site structure
    // and use its parent as the collectionId.
    const collectionId = React.useMemo(() => {
        if (props.collectionId) {
            return props.collectionId;
        }
        const spaces = getSiteSpacesFromStructure(target.siteStructure);
        const firstSpace = spaces[0];
        return firstSpace ? firstSpace.space.parent : undefined;
    }, [target.siteStructure, props.collectionId]);
    const [sectionSpace, setSectionSpace] = React.useState<api.Space | null>(null);
    const close = useDialogClose();
    const [isPending, startDialogAction] = useDialogAction();

    const onCreateSpace: SpaceCreationOnCreateSpace = (createSpace) => {
        startDialogAction(async () => {
            await toast.catch(async () => {
                const space = await createSpace({ collectionId });
                setSectionSpace(space);
            }, 'Error while creating space');
        });
    };

    const persistentSectionSpace = usePersistentValue(sectionSpace);
    return (
        <SpaceCreationDialogContainer isPending={isPending}>
            <DialogHeader>
                <Heading>Add section</Heading>
                <Description>
                    Each section is linked to a space — where your content lives. Choose where your
                    initial content comes from to get started quickly.
                </Description>
            </DialogHeader>
            <SpaceCreateDialogBody
                organizationId={organizationId}
                target={target}
                onCreateSpace={onCreateSpace}
                isPending={isPending}
                defaultOpenTranslation={defaultOpenTranslation}
            />
            {persistentSectionSpace ? (
                <DialogRoot
                    isOpen={Boolean(sectionSpace)}
                    onOpenChange={(isOpen) => {
                        if (!isOpen) {
                            setSectionSpace(null);
                            close();
                        }
                    }}
                >
                    <CreateSiteSectionDialog {...target.props} space={persistentSectionSpace} />
                </DialogRoot>
            ) : null}
        </SpaceCreationDialogContainer>
    );
}

function SpaceCreationDialogTargetVariant(
    props: Omit<SpaceCreationDialogProps, 'target'> & {
        target: SpaceCreationTargetSiteVariant;
    }
) {
    const { organizationId, collectionId, target, defaultOpenTranslation } = props;
    const [isPending, startDialogAction] = useDialogAction();

    const onCreateSpace: SpaceCreationOnCreateSpace = async (createSpace, finalize) => {
        await startDialogAction(async ({ close }) => {
            await toast.catch(async () => {
                const space = await createSpace({ collectionId });
                assert(
                    !finalize,
                    'Finalize callback is not supported when creating a space for a variant'
                );
                target.props.onCreateSpace(space);
                await close();
            }, 'Error while creating space');
        });
    };

    useSetAnalyticsScreen(
        analytics.createOrgEvent(organizationId, {
            screen: 'space_create',
        }),
        [organizationId]
    );

    const defaultSectionSpace = target.props.siteSection?.siteSpaces.find(
        (siteSpace) => siteSpace.default
    )?.space;

    return (
        <SpaceCreationDialogContainer isPending={isPending}>
            <DialogHeader>
                <Heading>Create variant space</Heading>
                <Description>
                    Create a new space that will be used as the content for this variant.
                </Description>
            </DialogHeader>
            <DialogBody
                data-testid="space_create.dialog"
                className="flex flex-col gap-10"
                scrollable={!isPending}
            >
                <SpaceCreationDialogSection>
                    <Heading>Quick actions</Heading>
                    <div className="relative grid grid-cols-3 gap-4">
                        {defaultSectionSpace ? (
                            <DuplicateCard
                                space={defaultSectionSpace}
                                onCreateSpace={onCreateSpace}
                            />
                        ) : null}
                        <TranslationCard
                            defaultSpace={defaultSectionSpace}
                            organizationId={organizationId}
                            onCreateSpace={onCreateSpace}
                            defaultOpen={defaultOpenTranslation}
                        />
                        <BlankCard organizationId={organizationId} onCreateSpace={onCreateSpace} />
                    </div>
                </SpaceCreationDialogSection>
                <React.Suspense fallback={null}>
                    <PickExistingSpaceSection
                        organizationId={organizationId}
                        target={target}
                        onCreateSpace={onCreateSpace}
                    />
                </React.Suspense>
            </DialogBody>
        </SpaceCreationDialogContainer>
    );
}

function SpaceCreationDialogContainer(props: { isPending: boolean; children: React.ReactNode }) {
    const { isPending, children } = props;
    return (
        <Dialog size={{ width: 880 }} className={isPending ? 'overflow-hidden' : undefined}>
            {isPending && (
                <div className="absolute inset-0 z-20 flex size-full flex-col items-center justify-center gap-2 rounded-md bg-base/70">
                    <LoadingIndicator size="xlarge" />
                    <div className="text-ui-standout">Your space is being created…</div>
                </div>
            )}
            {children}
        </Dialog>
    );
}

function SpaceCreateDialogBody(props: {
    organizationId: string;
    target: SpaceCreationTarget;
    onCreateSpace: SpaceCreationOnCreateSpace;
    isPending: boolean;
    defaultOpenTranslation?: boolean;
}) {
    const { organizationId, target, onCreateSpace, isPending, defaultOpenTranslation } = props;
    useSetAnalyticsScreen(
        analytics.createOrgEvent(organizationId, {
            screen: 'space_create',
        }),
        [organizationId]
    );
    return (
        <DialogBody
            data-testid="space_create.dialog"
            className="flex flex-col gap-10"
            scrollable={!isPending}
        >
            <TemplatesSection organizationId={organizationId} onCreateSpace={onCreateSpace} />
            <QuickActionsSection
                organizationId={organizationId}
                onCreateSpace={onCreateSpace}
                target={target}
                defaultOpenTranslation={defaultOpenTranslation}
            />
            <React.Suspense fallback={null}>
                <PickExistingSpaceSection
                    organizationId={organizationId}
                    target={target}
                    onCreateSpace={onCreateSpace}
                />
            </React.Suspense>
        </DialogBody>
    );
}

function TemplatesSection(props: {
    organizationId: string;
    onCreateSpace: SpaceCreationOnCreateSpace;
}) {
    const { organizationId, onCreateSpace } = props;
    return (
        <SpaceCreationDialogSection>
            <Heading>Start with a template</Heading>
            <TemplatesList>
                <BlankTemplateCard organizationId={organizationId} onCreateSpace={onCreateSpace} />
                {webConfig.contentTemplates.map((template) => (
                    <SpaceTemplateCard
                        key={template.id}
                        template={template}
                        onApplyTemplate={async (template) => {
                            onCreateSpace(async ({ collectionId }) => {
                                const { space } = await applyNewSpaceTemplate({
                                    template,
                                    organizationId,
                                    collectionId,
                                });
                                return space;
                            });
                        }}
                        organizationId={organizationId}
                    />
                ))}
            </TemplatesList>
        </SpaceCreationDialogSection>
    );
}

function QuickActionsSection(props: {
    organizationId: string;
    onCreateSpace: SpaceCreationOnCreateSpace;
    target: SpaceCreationTarget;
    defaultOpenTranslation?: boolean;
}) {
    const { organizationId, onCreateSpace, target, defaultOpenTranslation } = props;
    // To create a section, these quick actions don't make sense
    // - GitSync can be setup later, flow is totally async
    // - Translating a section is not a use case, it's at variant level
    // - Import is async, not suited for section creation flow, they can start blank and do it later
    if (target.type === 'siteSection') {
        return null;
    }
    return (
        <SpaceCreationDialogSection>
            <Heading>Quick actions</Heading>
            <div className="relative grid grid-cols-3 gap-4">
                <GitSyncCard organizationId={organizationId} onCreateSpace={onCreateSpace} />
                <TranslationCard
                    organizationId={organizationId}
                    onCreateSpace={onCreateSpace}
                    defaultOpen={defaultOpenTranslation}
                />
                <ImportCard organizationId={organizationId} onCreateSpace={onCreateSpace} />
            </div>
        </SpaceCreationDialogSection>
    );
}

function PickExistingSpaceSection(props: {
    organizationId: string;
    target: SpaceCreationTarget;
    onCreateSpace: SpaceCreationOnCreateSpace;
}) {
    const { organizationId, target, onCreateSpace } = props;
    const { data: orgAllContent } = useSuspenseQuery(
        apiQuery('getOrganizationAllContent', { organizationId })
    );
    const state = usePickExistingSpaceState({ target, orgAllContent });
    if (state.spaces.length === 0) {
        return null;
    }

    return (
        <SpaceCreationDialogSection>
            <Heading>
                {(() => {
                    switch (state.action) {
                        case 'duplicate':
                            return 'Duplicate an existing space';
                        case 'use':
                            return 'Pick an existing space';
                        default:
                            assertNever(state.action, `Unknown action: ${state.action}`);
                    }
                })()}
            </Heading>
            <PickExistingSpace
                state={state}
                organizationId={organizationId}
                onCreateSpace={onCreateSpace}
                collectionNameMap={extractCollectionNames(orgAllContent)}
            />
        </SpaceCreationDialogSection>
    );
}

function SpaceCreationDialogSection(props: React.ComponentPropsWithoutRef<'div'>) {
    return (
        <HeadingContext.Provider value={{ level: 3, className: 'text-ui-heading-medium' }}>
            <div {...props} className={clsx('flex flex-col gap-4', props.className)} />
        </HeadingContext.Provider>
    );
}
